import { z } from 'zod';
import { dnsZoneNameSchema, dnsRecordNameSchema, type DnsRecordDto } from './dns.js';

export const DNS_MANAGE_ZONES_FEATURE = 'dns_manage_zones';
export const dnsInventoryQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  subscriptionId: z.string().uuid().optional(),
  clientId: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const assignDnsDomainSchema = z
  .object({ name: dnsZoneNameSchema, subscriptionId: z.string().uuid() })
  .strict();
export const deleteDnsZoneSchema = z.object({ confirmName: dnsZoneNameSchema }).strict();
export const dnsRevisionSchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
});

/** Absolute owner input is accepted only inside the selected zone. */
export function normalizeDnsOwner(value: string, zone: string): string {
  const input = value.trim().toLowerCase();
  const bare = input.replace(/\.$/, '');
  const relative =
    bare === zone ? '@' : bare.endsWith(`.${zone}`) ? bare.slice(0, -(zone.length + 1)) : input;
  if (input.endsWith('.') && relative === input)
    throw new Error('The hostname must be inside this domain');
  const name = dnsRecordNameSchema.parse(relative);
  if ((name === '@' ? zone : `${name}.${zone}`).length > 253)
    throw new Error('The hostname is too long');
  return name;
}
export type DnsPublicationState =
  | 'pending'
  | 'synchronized'
  | 'drift'
  | 'error'
  | 'paused'
  | 'unconfigured'
  | 'orphaned';
export interface DnsCapability {
  allowed: boolean;
  reason: string | null;
}
export interface DnsPublicationDto {
  state: DnsPublicationState;
  checkedAt: string | null;
  publishedAt: string | null;
  reason: string | null;
}
export interface DnsWorkspaceDomainDto {
  id: string;
  name: string;
  subscriptionId: string;
  subscriptionName: string;
  customerName: string | null;
  zoneId: string | null;
  recordCount: number;
  revision: number;
  primary: DnsPublicationDto;
  publication: DnsPublicationDto;
  capabilities: {
    createZone: DnsCapability;
    manageRecords: DnsCapability;
    removeAssignment: DnsCapability;
    manageSettings: DnsCapability;
  };
}
export interface DnsWorkspaceRecordDto extends DnsRecordDto {
  effectiveTtl: number;
  sourceLabel: string;
  sourceLink: string | null;
  capability: DnsCapability;
}
export interface DnsWorkspaceZoneDto extends DnsWorkspaceDomainDto {
  records: DnsWorkspaceRecordDto[];
  nameservers: string[];
  dsRecords: string[];
  signingMode: string | null;
  targets: Array<{ label: string; publication: DnsPublicationDto }>;
}
export interface DnsInventoryDto {
  items: DnsWorkspaceDomainDto[];
  total: number;
  page: number;
  pageSize: number;
}
